import React from "react";
import { observer } from "mobx-react";
import cns from "classnames";
import {
  Checkbox,
  Input,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  withStyles
} from "@material-ui/core";
import { Warning } from "@material-ui/icons";
import { professions } from "@bonliva-crm/common";
import { Classes, ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { Body } from "@components";
import ReactSelectMui from "@components/Select/ReactSelectMui";
import WithPopover from "@components/WithPopover";
import { CustomerEditorStore } from "../CustomerEditorStore";

type Props = {
  classes: Classes<typeof styles>;
  store: CustomerEditorStore;
};

@observer
class CustomerAOESettings extends React.Component<Props> {
  totalColumns: number;

  constructor(props: Props) {
    super(props);

    this.totalColumns = 8;
  }

  setEnabled = (prof: string, aoe: string, value: boolean) => {
    return this.props.store.setAOESettingsField(prof, aoe, "Enabled", value);
  };

  setGroupInvoice = (prof: string, aoe: string, value: boolean) => {
    return this.props.store.setAOESettingsField(prof, aoe, "GroupInvoicing", value);
  };

  setTimeReport = (prof: string, aoe: string, value: boolean) => {
    return this.props.store.setAOESettingsField(prof, aoe, "timeReport", value);
  };

  render() {
    const { store } = this.props;

    const professions = store.values.Professions.slice().sortBy((x) => x);

    return (
      <Table size="small" data-test="CustomerAOESettings">
        {this.renderHeader()}

        <TableBody>
          {(!store.values.Professions || store.values.Professions.length === 0) &&
            this.renderEmpty()}

          {professions.map((profession) => {
            return (
              <React.Fragment key={profession}>
                {this.renderSubheader(profession)}

                {store
                  .getAOEsOnProfession(profession)
                  .map((expertise) => this.renderRow(profession, expertise))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  private renderHeader = () => {
    const { classes, store } = this.props;

    return (
      <TableHead>
        <TableRow className={classes.header}>
          <TableCell padding="checkbox" />
          <TableCell style={{ width: "20%" }}>{locale.AreaOfExpertise}</TableCell>
          <TableCell style={{ width: "20%" }}>{locale["Responsible BA"]}</TableCell>
          <TableCell>{locale.FortnoxNumber}</TableCell>
          <TableCell>{locale.InvoiceRef}</TableCell>
          {store.isOrderId && <TableCell>{locale.OrderId}</TableCell>}
          <TableCell>{locale.bookingInvoice.consolidatedInvoice}</TableCell>
          <TableCell>{locale.TimeReport}</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  private renderSubheader = (profession: string) => {
    return (
      <TableRow>
        <TableCell
          colSpan={this.totalColumns}
          align="center"
          style={{ fontWeight: "bold" }}
        >
          {professions.getProfessionDisplayName(profession)}
        </TableCell>
      </TableRow>
    );
  };

  private renderEmpty = () => {
    return (
      <TableRow>
        <TableCell colSpan={this.totalColumns} align="center">
          {"Välj yrken ovanför"}
        </TableCell>
      </TableRow>
    );
  };

  private renderRow = (profession: string, areaOfExpertise: string) => {
    const { classes, store } = this.props;

    const data = store.getAOESettings(profession, areaOfExpertise);
    const isAOEDisabled = store.isAOEDisabled(areaOfExpertise);

    // an extra <span> here fixes the alignment in checkboxes that are wrapped into WithPopover
    let checkbox = (
      <span>
        <Checkbox
          checked={data.Enabled}
          onChange={(v) => this.setEnabled(profession, areaOfExpertise, v.target.checked)}
          disabled={isAOEDisabled}
        />
      </span>
    );

    if (isAOEDisabled) {
      checkbox = (
        <WithPopover
          in
          popoverContent={<Body>{locale.Disabled}</Body>}
          PopoverProps={{
            anchorOrigin: { horizontal: "center", vertical: "bottom" },
            transformOrigin: { horizontal: "left", vertical: "center" }
          }}
        >
          {checkbox}
        </WithPopover>
      );
    }

    return (
      <TableRow
        key={`${profession}_${areaOfExpertise}`}
        hover
        data-test-id={`${profession}.${areaOfExpertise}`}
        className={cns(
          {
            [classes.rowError]: data.Error
          },
          classes.row
        )}
      >
        <TableCell
          padding="checkbox"
          onClick={() => this.setEnabled(profession, areaOfExpertise, true)}
        >
          {checkbox}
        </TableCell>

        <TableCell onClick={() => this.setEnabled(profession, areaOfExpertise, true)}>
          <div className={classes.title}>
            {data.Error && <Warning fontSize="small" color="error" />}
            {areaOfExpertise}
          </div>
        </TableCell>

        <TableCell onClick={() => this.setEnabled(profession, areaOfExpertise, true)}>
          <ReactSelectMui
            disabled={!data.Enabled}
            options={store.enabledUsers}
            placeholder={locale["Responsible BA"]}
            onChange={(v) =>
              store.setAOESettingsField(
                profession,
                areaOfExpertise,
                "ResponsiblePerson",
                v
              )
            }
            multi={false}
            value={data.ResponsiblePerson}
          />
        </TableCell>

        <TableCell onClick={() => this.setEnabled(profession, areaOfExpertise, true)}>
          <Input
            disabled={!data.Enabled || !store.canEditCustomerNumber}
            placeholder={locale.FortnoxNumber}
            fullWidth
            onChange={(v) =>
              store.setAOESettingsField(
                profession,
                areaOfExpertise,
                "FortnoxId",
                v.target.value
              )
            }
            name="FortnoxId"
            value={data.FortnoxId}
          />
        </TableCell>

        <TableCell onClick={() => this.setEnabled(profession, areaOfExpertise, true)}>
          <Input
            disabled={!data.Enabled || !store.canEditCustomerNumber}
            placeholder={locale.InvoicingData}
            fullWidth
            onChange={(v) =>
              store.setAOESettingsField(
                profession,
                areaOfExpertise,
                "InvoicingData",
                v.target.value
              )
            }
            name="InvoicingData"
            value={data.InvoicingData}
          />
        </TableCell>
        {store.isOrderId && (
          <TableCell onClick={() => this.setEnabled(profession, areaOfExpertise, true)}>
            <Input
              disabled={!data.Enabled || !store.canEditCustomerNumber}
              placeholder={locale.OrderId}
              fullWidth
              onChange={(v) =>
                store.setAOESettingsField(
                  profession,
                  areaOfExpertise,
                  "orderId",
                  v.target.value
                )
              }
              name="orderId"
              value={data.orderId}
            />
          </TableCell>
        )}

        <TableCell padding="checkbox">
          <span>
            <Checkbox
              checked={data.GroupInvoicing}
              onChange={(e) => {
                this.setGroupInvoice(profession, areaOfExpertise, e.target.checked);
              }}
              disabled={!store.canEditGroupInvoicing}
            />
          </span>
        </TableCell>

        <TableCell padding="checkbox">
          <span>
            <Checkbox
              checked={data.timeReport}
              onChange={(e) => {
                this.setTimeReport(profession, areaOfExpertise, e.target.checked);
              }}
              // disabled={!store.canEditGroupInvoicing}
            />
          </span>
        </TableCell>
      </TableRow>
    );
  };
}

const styles = (theme: ThemeType) => ({
  header: {
    backgroundColor: theme.palette.secondary.background,

    "& > td": {
      color: theme.palette.secondary.contrast
    }
  },
  row: {
    "& > td": {
      padding: 3
    }
  },

  rowError: {
    backgroundColor: theme.palette.error.background
  },

  title: {
    alignItems: "center",
    display: "inline-flex"
  }
});

export default withStyles(styles as any)(CustomerAOESettings);
